import { Icon } from '@petsmart-ui/sparky';

import { SideDrawer } from '@libs/shared-ui';

import { trackNavigationHeaderClick } from '../../../../../shared';
import { type DrawerNode, isGroupNode } from '../drawer.types';
import { Level, type LevelEntry } from '../shared';
import { OverviewLink } from './overview-link';
import { useNestedLevel } from './use-nested-level';
import { VisualItemsGrid } from './visual-items-grid';

import './nested-level.scss';

type NestedLevelProps = {
    node: DrawerNode;
    rootIsOpen: boolean;
    selectionPath: string[];
    onBack: () => void;
    onCloseAll: () => void;
};

export function NestedLevel({ node, rootIsOpen, selectionPath, onBack, onCloseAll }: Readonly<NestedLevelProps>) {
    const { activeNode, closeChild, handleBack, handleOpenChange, handleOpenChangeComplete, isOpen, openNode } =
        useNestedLevel({ rootIsOpen, onBack, onCloseAll });
    const isGroup = isGroupNode(node);
    const overviewLink = isGroup ? node.overviewLink : node.kind === 'panel' ? node.panel.overviewLink : undefined;

    function trackNavLink(href: string, label: string) {
        trackNavigationHeaderClick({
            selection: [...selectionPath, label],
            linkUrl: href,
            navSection: 'main nav',
        });
        onCloseAll();
    }

    return (
        <>
            <SideDrawer
                className="mobile-menu-drawer"
                isOpen={isOpen}
                showBackdrop={false}
                onOpenChange={handleOpenChange}
                onOpenChangeComplete={handleOpenChangeComplete}
            >
                <SideDrawer.Header className="mobile-menu-drawer__header mobile-menu-drawer__header--nested">
                    <button type="button" aria-label="Back" className="mobile-menu-drawer__back" onClick={handleBack}>
                        <Icon name="chevron-left" size="lg" aria-hidden="true" />
                    </button>
                    <SideDrawer.Title className="mobile-menu-drawer__level-title">{node.title}</SideDrawer.Title>
                </SideDrawer.Header>
                <SideDrawer.Body>
                    {overviewLink && (
                        <OverviewLink
                            link={overviewLink}
                            onSelect={() => trackNavLink(overviewLink.href, overviewLink.label)}
                        />
                    )}

                    {isGroup && (
                        <Level
                            label={node.title}
                            entries={node.links.map(
                                (link): LevelEntry => ({
                                    href: link.href,
                                    id: `${link.href}::${link.label}`,
                                    label: link.label,
                                    onSelect: () => trackNavLink(link.href, link.label),
                                    openInNewTab: link.openInNewTab,
                                })
                            )}
                        />
                    )}

                    {!isGroup && node.kind === 'sidebar' && (
                        <Level
                            label={node.title}
                            entries={node.sidebarItems.map(
                                (item): LevelEntry => ({
                                    id: item.id,
                                    label: item.label,
                                    onSelect: trigger =>
                                        openNode(
                                            { id: item.id, kind: 'panel', panel: item.panel, title: item.label },
                                            trigger
                                        ),
                                })
                            )}
                        />
                    )}

                    {!isGroup && node.kind === 'panel' && node.panel.content.kind === 'columns' && (
                        <Level
                            label={node.title}
                            entries={node.panel.content.columns.flat().map(
                                (group, index): LevelEntry => ({
                                    id: `${group.title}-${index}`,
                                    label: group.title,
                                    onSelect: trigger => openNode(group, trigger),
                                })
                            )}
                        />
                    )}

                    {!isGroup &&
                        node.kind === 'panel' &&
                        node.panel.content.kind === 'visual' &&
                        (node.panel.content.layout === 'imageOnly' ? (
                            <VisualItemsGrid
                                items={node.panel.content.items}
                                selectionPath={selectionPath}
                                onLinkSelect={onCloseAll}
                            />
                        ) : (
                            <Level
                                label={node.title}
                                entries={node.panel.content.items.map(
                                    (item): LevelEntry => ({
                                        href: item.href,
                                        id: `${item.href}::${item.title ?? item.label}`,
                                        label: item.title ?? item.label,
                                        onSelect: () => trackNavLink(item.href, item.title ?? item.label),
                                        openInNewTab: item.openInNewTab,
                                    })
                                )}
                            />
                        ))}
                </SideDrawer.Body>
            </SideDrawer>

            {activeNode && (
                <NestedLevel
                    node={activeNode}
                    rootIsOpen={rootIsOpen}
                    selectionPath={[...selectionPath, activeNode.title]}
                    onBack={closeChild}
                    onCloseAll={onCloseAll}
                />
            )}
        </>
    );
}
